/**
 * Interpreter node types - provides type safety for the procedure interpreter.
 *
 * This module defines:
 * 1. A const array of all known node types for runtime validation
 * 2. A discriminated union type for compile-time exhaustive handling
 * 3. A Zod schema for runtime validation
 *
 * When adding a new node type:
 * 1. Add to `interpreterNodeTypes` const array
 * 2. Add to `InterpreterNode` union type
 * 3. Add to `InterpreterNodeSchema` union
 * 4. Add case to interpreter's node handler switch (compiler will enforce this)
 */
import * as z from "zod";

import {
  ConditionalNodeSchema,
  type ConditionalNode,
} from "./conditional/conditional-node-schema";
import {
  ExampleNodeSchema,
  type ExampleNode,
} from "./example/example-node-schema";
import { HeadingNodeSchema, type HeadingNode } from "./heading-node-schema";
import {
  HorizontalRuleNodeSchema,
  type HorizontalRuleNode,
} from "./horizontal-rule-node-schema";
import {
  BulletListNodeSchema,
  ListItemNodeSchema,
  OrderedListNodeSchema,
  type BulletListNode,
  type ListItemNode,
  type OrderedListNode,
} from "./list-node-schema";
import { MentionNodeSchema, type MentionNode } from "./mention-node-schema";
import { NewlineNodeSchema, type NewlineNode } from "./newline-node-schema";
import {
  ParagraphNodeSchema,
  type ParagraphNode,
} from "./paragraph-node-schema";
import {
  SectionNodeSchema,
  type SectionNode,
} from "./section/section-node-schema";
import {
  StepSelectNodeSchema,
  type StepSelectNode,
} from "./step-select-node-schema";
import { TextNodeSchema, type TextNode } from "./text-node-schema";
import { ToolNodeSchema, type ToolNode } from "./tool-node-schema";
import { VariableNodeSchema, type VariableNode } from "./variable-node-schema";

// =============================================================================
// NODE TYPE CONSTANTS
// =============================================================================

/**
 * All known node types that the interpreter can handle.
 * Used for runtime validation and exhaustive checking.
 */
export const interpreterNodeTypes = [
  "text",
  "variable",
  "tool",
  "mention",
  "section",
  "conditional",
  "stepSelect",
  "example",
  "paragraph",
  "heading",
  "horizontalRule",
  "bulletList",
  "orderedList",
  "listItem",
  "newline",
] as const;

/**
 * Type representing any known node type string.
 */
export type InterpreterNodeType = (typeof interpreterNodeTypes)[number];

// =============================================================================
// DISCRIMINATED UNION TYPE
// =============================================================================

/**
 * Discriminated union of all node types the interpreter can handle.
 *
 * Use exhaustive switch on `node.type` to ensure all types are handled:
 * ```typescript
 * function handleNode(node: InterpreterNode): string {
 *   switch (node.type) {
 *     case "text": return node.value;
 *     case "variable": return populateVariable(node);
 *     // ... all cases
 *     default: {
 *       const _exhaustive: never = node;
 *       throw new Error(`Unhandled: ${_exhaustive}`);
 *     }
 *   }
 * }
 * ```
 */
export type InterpreterNode =
  | TextNode
  | VariableNode
  | ToolNode
  | MentionNode
  | SectionNode
  | ConditionalNode
  | StepSelectNode
  | ExampleNode
  | ParagraphNode
  | HeadingNode
  | HorizontalRuleNode
  | BulletListNode
  | OrderedListNode
  | ListItemNode
  | NewlineNode;

// =============================================================================
// ZOD SCHEMA FOR RUNTIME VALIDATION
// =============================================================================

/**
 * Zod schema for validating interpreter nodes.
 * Uses union to validate against all known node schemas.
 *
 * For performance, the most common node types are listed first.
 */
export const InterpreterNodeSchema: z.ZodType<InterpreterNode> = z.union([
  // Common inline nodes (most frequent)
  TextNodeSchema,
  VariableNodeSchema,
  ParagraphNodeSchema,

  // Structural nodes
  SectionNodeSchema,
  ConditionalNodeSchema,
  StepSelectNodeSchema,
  ExampleNodeSchema,

  // Other inline nodes
  ToolNodeSchema,
  MentionNodeSchema,
  HeadingNodeSchema,

  // List nodes
  BulletListNodeSchema,
  OrderedListNodeSchema,
  ListItemNodeSchema,

  // Simple nodes
  HorizontalRuleNodeSchema,
  NewlineNodeSchema,
]);

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a node type string is a known interpreter node type.
 */
export function isInterpreterNodeType(
  type: string,
): type is InterpreterNodeType {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return interpreterNodeTypes.includes(type as InterpreterNodeType);
}

/**
 * Validates and narrows a generic node to InterpreterNode.
 * Throws if the node type is unknown.
 *
 * @throws {Error} If the node type is not in interpreterNodeTypes
 */
export function validateInterpreterNode(node: {
  type: string;
  [key: string]: unknown;
}): InterpreterNode {
  const result = InterpreterNodeSchema.safeParse(node);
  if (!result.success) {
    throw new Error(
      `Unknown or invalid node type "${node.type}": ${result.error.message}`,
    );
  }
  return result.data;
}

// =============================================================================
// STEP INPUT TYPES (for step type handling)
// =============================================================================

/**
 * Known step input types from StepTypeConfig.inputType.
 * Used by the interpreter to determine how to handle each step.
 */
export const stepInputTypes = [
  "freeform",
  "select",
  "condition",
  "stop",
  "example",
] as const;

/**
 * Type representing a step input type.
 */
export type StepInputType = (typeof stepInputTypes)[number];

/**
 * Type guard for step input types.
 */
export function isStepInputType(value: string): value is StepInputType {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return stepInputTypes.includes(value as StepInputType);
}
